import { Chain, isMonacoChain } from "@nitro-ape/shared/models/enums/chain";
import { formatMonacoSymbolForUrl } from "@nitro-ape/shared/features/monaco/api/utils";
import { HYPERLIQUID_ENABLED } from "@/lib/feature-flags";

/**
 * Utility function to build trade URLs with the new dynamic route format
 */
export function buildTradeUrl(chain: string, address: string): string {
  // For Monaco chains, convert "BTC/USDC" → "BTC-USDC" for clean URLs
  const urlAddress = isMonacoChain(chain as Chain)
    ? formatMonacoSymbolForUrl(address)
    : encodeURIComponent(address);
  return `/trade/${encodeURIComponent(chain)}/${urlAddress}`;
}

// TODO: Add default tokens for each chain
export const DEFAULT_SPOT_TOKENS: Partial<Record<Chain, string>> = {
  [Chain.solana]: "sol",
  [Chain.sei]: "sei",
  [Chain.monaco_spot]: "BTC-USDC",
};

export const DEFAULT_PERP_SYMBOL = "BTC";
export const DEFAULT_PERP_CHAIN = HYPERLIQUID_ENABLED
  ? Chain.hyperliquid
  : Chain.monaco_perps;

export function buildSpotTradeUrl(
  chain: Chain,
  tokenAddress: string | undefined,
): string {
  const address = tokenAddress ?? DEFAULT_SPOT_TOKENS[chain];
  if (!address) {
    return "/explore"; //! Should never happen
  }
  // For Monaco chains, ensure symbol is URL-formatted (BTC-USDC not BTC/USDC)
  const urlAddress = isMonacoChain(chain)
    ? formatMonacoSymbolForUrl(address)
    : address;
  return `/trade/${chain}/${urlAddress}`;
}

export function buildPerpTradeUrl(symbol: string | undefined): string {
  if (!HYPERLIQUID_ENABLED) {
    return POST_LOGIN_REDIRECT_PATH;
  }
  return `/trade/${DEFAULT_PERP_CHAIN}/${symbol ?? DEFAULT_PERP_SYMBOL}`;
}

export const DEFAULT_MONACO_PERP_SYMBOL = "BTC-USDC";

export function buildMonacoPerpUrl(symbol: string | undefined): string {
  const raw = symbol ?? DEFAULT_MONACO_PERP_SYMBOL;
  return `/monaco-perpetuals/${formatMonacoSymbolForUrl(raw)}`;
}

export const DEFAULT_HYPERLIQUID_PERP_SYMBOL = "BTC";

export function buildHyperliquidPerpUrl(symbol: string | undefined): string {
  return `/perpetuals/${symbol ?? DEFAULT_HYPERLIQUID_PERP_SYMBOL}`;
}

/**
 * Resolves the URL the "Perps" navbar entry should link to: most recent
 * pair on the chain the user was last viewing (Hyperliquid or Monaco
 * perps). Falls back to the post-login default (Monaco BTC-USDC) when
 * the user has no history yet.
 */
export function buildLastPerpsTradeUrl(opts: {
  lastPerpsChain: Chain | null;
  recentHyperliquidPerps: string[];
  recentMonacoPerps: string[];
}): string {
  const { lastPerpsChain, recentHyperliquidPerps, recentMonacoPerps } = opts;

  if (
    HYPERLIQUID_ENABLED &&
    lastPerpsChain === Chain.hyperliquid &&
    recentHyperliquidPerps.length > 0
  ) {
    return buildHyperliquidPerpUrl(recentHyperliquidPerps[0]);
  }

  if (lastPerpsChain === Chain.monaco_perps && recentMonacoPerps.length > 0) {
    return buildMonacoPerpUrl(recentMonacoPerps[0]);
  }

  // No explicit "last chain" recorded — pick whichever chain has a
  // history, preferring Monaco (matches our post-login default).
  if (recentMonacoPerps.length > 0) {
    return buildMonacoPerpUrl(recentMonacoPerps[0]);
  }
  if (HYPERLIQUID_ENABLED && recentHyperliquidPerps.length > 0) {
    return buildHyperliquidPerpUrl(recentHyperliquidPerps[0]);
  }

  return POST_LOGIN_REDIRECT_PATH;
}

// Single source of truth for "where to send a user after a successful login,
// or when they hit a route that needs an authenticated landing surface
// (root `/`, `/login` when already signed in, ref-only `/p/:ref` invites).
// Update this constant to change the post-login experience everywhere.
export const POST_LOGIN_REDIRECT_PATH = buildMonacoPerpUrl(
  DEFAULT_MONACO_PERP_SYMBOL,
);
